// ✅ VerificationPassword.jsx — подтверждение кода для восстановления пароля с временной зоной
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./verificationPassword.css";
import verification_mark from "../../assets/verification_mark.png";
import { IoMdLock } from "react-icons/io";
import { CiCircleQuestion } from "react-icons/ci";
import toast from "react-hot-toast";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { useTranslation } from "react-i18next";
import { OtpInput } from "../../components/input/otpInput/OtpInput";

const BASE_URL = REST_API_URL;

const getUserTimeZone = () => {
  if (typeof window === "undefined") return "UTC";
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export default function VerificationPassword() {
  const [timeLeft, setTimeLeft] = useState(20);
  const [tick, setTick] = useState(false);
  const [resending, setResending] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState("UTC");
  const [status, setStatus] = useState("idle");
  const [otpKey, setOtpKey] = useState(0);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const email = localStorage.getItem("pendingEmail");

  useEffect(() => {
    const timeZone = getUserTimeZone();
    setUserTimeZone(timeZone);

    localStorage.setItem(
      "password_recovery_verification_start",
      new Date().toISOString(),
    );
    localStorage.setItem("password_recovery_timezone", timeZone);
  }, []);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft((t) => t - 1);
        setTick(true);
        setTimeout(() => setTick(false), 300);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const handleOtpComplete = async (otpCode) => {
    setStatus("loading");
    const startTime = Date.now();

    try {
      const headers = buildHeaders();

      const res = await fetch(`${BASE_URL}/auth/verify-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          code: otpCode,
          purpose: "password_recovery",
          timeZone: userTimeZone,
        }),
      });

      const data = await res.json().catch(() => ({}));

      const elapsed = Date.now() - startTime;
      const minLoading = 500;
      if (elapsed < minLoading) {
        await new Promise((resolve) => setTimeout(resolve, minLoading - elapsed));
      }

      if (!res.ok) {
        const errorMessage = data?.detail || t("auth.errorInvalidCode");
        toast.error(errorMessage);
        setStatus("error");
        localStorage.setItem(
          "password_recovery_verification_failed",
          new Date().toISOString(),
        );

        if (window.gtag) {
          window.gtag("event", "password_recovery_verification_error", {
            error_type: "invalid_code",
          });
        }

        return;
      }
      
      setStatus("success");
      toast.success(t("auth.successVerification") || "Verification successfully");
      localStorage.setItem("recoveryCode", otpCode);

      localStorage.setItem(
        "password_recovery_verification_success",
        new Date().toISOString(),
      );
      localStorage.setItem("recovery_code_verification_timezone", userTimeZone);

      if (data?.access && data?.refresh) {
        localStorage.setItem("accessToken", data.access);
        localStorage.setItem("refreshToken", data.refresh);
      }

      if (window.gtag) {
        window.gtag("event", "password_recovery_code_verified", {
          email_domain: email?.split("@")[1],
        });
      }

      setTimeout(() => {
        navigate("/reset-password", {
          state: {
            timeZone: userTimeZone,
            verificationTimestamp: new Date().toISOString(),
          },
        });
      }, 2200);
    } catch (err) {
      console.log("❌ VERIFY ERROR:", err);
      const elapsed = Date.now() - startTime;
      const minLoading = 500;
      if (elapsed < minLoading) {
        await new Promise((resolve) => setTimeout(resolve, minLoading - elapsed));
      }
      toast.error(t("auth.errorVerificationFailed") || "Verification failed. Try again later.");
      setStatus("error");

      if (window.gtag) {
        window.gtag("event", "password_recovery_verification_error", {
          error_type: "network",
        });
      }
    }
  };

  const handleResend = async () => {
    if (timeLeft > 0 || resending) return;
    setResending(true);

    try {
      const headers = buildHeaders();

      const res = await fetch(`${BASE_URL}/auth/resend-code/`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          email,
          purpose: "password_recovery",
          timeZone: userTimeZone,
        }),
      });

      if (!res.ok) throw new Error();

      setTimeLeft(20);
      setStatus("idle");
      setOtpKey((k) => k + 1);

      localStorage.setItem(
        "password_recovery_code_resend",
        new Date().toISOString(),
      );
      localStorage.setItem(
        "password_recovery_resend_count",
        (
          parseInt(
            localStorage.getItem("password_recovery_resend_count") || 0,
          ) + 1
        ).toString(),
      );

      if (window.gtag) {
        window.gtag("event", "password_recovery_code_resent", {
          email_domain: email?.split("@")[1],
        });
      }
      toast.success(t("auth.successCodeResent") || "Verification code resent!");
    } catch {
      toast.error(t("auth.errorResendFailed") || "Failed to resend code. Please try again.");
    } finally {
      setTimeout(() => setResending(false), 1000);
    }
  };

  return (
    <div className="verify">
      <main className="verify-main">
        <div className="verify-card">
          <OtpInput
            key={otpKey}
            length={6}
            status={status}
            onComplete={handleOtpComplete}
            onReset={() => setStatus("idle")}
            timeLeft={timeLeft}
            tick={tick}
            resending={resending}
            onResend={handleResend}
            resendLabel={t("auth.sendCodeAgain")}
            resendBtnLabel={t("auth.resendCode")}
            successMessage={t("auth.successVerification") || "Verification successfully"}
            avatarNode={<img src={verification_mark} alt="icon" />}
            title={t("auth.verificationCodeTitle")}
            subtitle={t("auth.verificationSubtitle", { email: email })}
          />
        </div>
      </main>

      <footer className="verify-footer">
        <span>{t("auth.copyright")}</span>
        <div>
          <a href="#">
            <IoMdLock /> {t("auth.privacy")}
          </a>
          <a href="#">
            <CiCircleQuestion /> {t("auth.getHelp")}
          </a>
        </div>
      </footer>
    </div>
  );
}
